import { useState } from "react";
import { useParams } from "react-router-dom";
import { Check, Send, ShieldCheck, X } from "lucide-react";

import { apiErrorHandler } from "@/core/api/api-error-handler";
import { LoadScreen, PageBackButton } from "@/core/components/shared";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import { NoTranslate } from "@/core/components/ui/no-translate";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useKycCase } from "@/features/kyc/api/use-kyc-case";
import { useParty } from "@/features/kyc/api/use-party";
import { useSubmitKycCase } from "@/features/kyc/api/use-submit-kyc-case";
import type { ReviewKycCaseRequestDto } from "@/features/kyc/api/dto";
import { KycCaseReviewDialog } from "@/features/kyc/components";
import { KycCaseDocuments } from "@/features/kyc/components/kyc-documents/kyc-case-documents";
// Phase 16 — Delta v2.5 : voie principale worker kyc-service (apply-kyc-approval).
import { useApplyKycApproval } from "@/features/wallet/api/use-apply-kyc-approval";

/**
 * Détail d'un dossier KYC (`GET /KycCases/{id}`) — statut, canal, niveau,
 * dates et notes du réviseur. Actions selon le cycle de vie : Soumettre
 * (DRAFT), Approuver/Rejeter (SUBMITTED, IN_REVIEW). Les pièces
 * justificatives sont gérées par `KycCaseDocuments` en bas de page.
 */
export function KycCaseDetailPage() {
  const { t } = useTypedTranslation();
  const { kycCaseId = "" } = useParams();
  const { data: kycCase, isLoading, error, refetch } = useKycCase(kycCaseId);
  const submitKycCase = useSubmitKycCase(kycCaseId);
  const [reviewDecision, setReviewDecision] =
    useState<ReviewKycCaseRequestDto["decision"] | null>(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [applyKycOpen, setApplyKycOpen] = useState(false);
  const applyKyc = useApplyKycApproval();
  const { data: party } = useParty(
    kycCase?.partyId ?? "",
    Boolean(kycCase?.partyId) && kycCase?.status === "APPROVED"
  );
  const backButton = (
    <PageBackButton to="/app/kyc/cases" label={t("common.back")} />
  );

  if (isLoading) {
    return (
      <section className="flex flex-col gap-4">
        {backButton}
        <div className="flex h-64 items-center justify-center">
          <LoadScreen />
        </div>
      </section>
    );
  }

  if (error || !kycCase) {
    return (
      <section className="flex flex-col gap-4">
        {backButton}
        <div className="flex flex-col items-center gap-4 py-24">
          <p className="font-semibold text-destructive">
            {error?.message ?? t("error")}
          </p>
          <Button variant="secondary" size="sm" onClick={() => void refetch()}>
            {t("reload")}
          </Button>
        </div>
      </section>
    );
  }

  const canSubmit = kycCase.status === "DRAFT";
  const canReview =
    kycCase.status === "SUBMITTED" || kycCase.status === "IN_REVIEW";
  const canApplyKyc =
    kycCase.status === "APPROVED" && Boolean(party?.keycloakUserId);

  const fields: { label: string; value?: string | null }[] = [
    { label: t("kyc.cases.form.partyId"), value: kycCase.partyId },
    { label: t("kyc.cases.form.assignedAgentId"), value: kycCase.assignedAgentId },
    {
      label: t("kyc.cases.columns.submittedAt"),
      value: kycCase.submittedAt
        ? formatDateWithFallback(kycCase.submittedAt, "—")
        : null,
    },
    {
      label: t("kyc.cases.detail.reviewedAt"),
      value: kycCase.reviewedAt
        ? formatDateWithFallback(kycCase.reviewedAt, "—")
        : null,
    },
    {
      label: t("kyc.cases.detail.reviewedBy"),
      value: kycCase.reviewedByKeycloakId,
    },
    { label: t("kyc.cases.detail.reviewerNotes"), value: kycCase.reviewerNotes },
    { label: t("kyc.cases.detail.expiryDate"), value: kycCase.expiryDate },
  ];

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {backButton}
          <h1 className="text-2xl font-semibold text-foreground">
            <NoTranslate as="span" className="font-mono text-xl">
              {kycCase.id}
            </NoTranslate>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {canSubmit ? (
            <Button onClick={() => setSubmitOpen(true)} disabled={submitKycCase.isPending}>
              {submitKycCase.isPending ? (
                <ButtonSpinner />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              {t("kyc.cases.submit")}
            </Button>
          ) : null}
          {canReview ? (
            <RequireRole roles={[FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN]}>
              <Button
                variant="outline"
                onClick={() => setReviewDecision("APPROVED")}
              >
                <Check className="mr-2 h-4 w-4" />
                {t("kyc.cases.approve")}
              </Button>
              <Button
                variant="destructive"
                onClick={() => setReviewDecision("REJECTED")}
              >
                <X className="mr-2 h-4 w-4" />
                {t("kyc.cases.reject")}
              </Button>
            </RequireRole>
          ) : null}
          {canApplyKyc ? (
            <RequireRole
              roles={[FLASHPAY_ROLES.SERVICE, FLASHPAY_ROLES.SUPERADMIN]}
            >
              <Button
                variant="outline"
                onClick={() => setApplyKycOpen(true)}
                disabled={applyKyc.isPending}
              >
                {applyKyc.isPending ? (
                  <ButtonSpinner />
                ) : (
                  <ShieldCheck className="mr-2 h-4 w-4" />
                )}
                {t("kyc.cases.applyKycApproval.action" as never)}
              </Button>
            </RequireRole>
          ) : null}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <NoTranslate as="span">
              <Badge variant="secondary">{kycCase.levelRequested}</Badge>
            </NoTranslate>
            <NoTranslate as="span">
              <Badge variant="outline">{kycCase.status}</Badge>
            </NoTranslate>
            <NoTranslate as="span">
              <Badge variant="outline">{kycCase.channel}</Badge>
            </NoTranslate>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {fields.map(({ label, value }) => (
              <div key={label}>
                <dt className="text-sm text-muted-foreground">{label}</dt>
                <dd className="font-medium">
                  <NoTranslate as="span">{value || "—"}</NoTranslate>
                </dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      <KycCaseDocuments
        kycCaseId={kycCaseId}
        documents={kycCase.documents}
        caseStatus={kycCase.status}
      />

      {reviewDecision ? (
        <KycCaseReviewDialog
          open={Boolean(reviewDecision)}
          onOpenChange={(open) => {
            if (!open) setReviewDecision(null);
          }}
          kycCaseId={kycCaseId}
          decision={reviewDecision}
        />
      ) : null}

      <MutationConfirmPreview
        open={submitOpen}
        onOpenChange={setSubmitOpen}
        data={{
          id: kycCase.id,
          partyId: kycCase.partyId,
          status: kycCase.status,
          levelRequested: kycCase.levelRequested,
        }}
        title={t("common.mutationPreview.title")}
        description={t("common.mutationPreview.description")}
        confirmLabel={t("kyc.cases.submit")}
        t={(key) => t(key as never)}
        pending={submitKycCase.isPending}
        onConfirm={() =>
          submitKycCase.mutate(undefined, {
            onSuccess: () => {
              notifSilence(t("kyc.cases.feedback.submitted"));
              setSubmitOpen(false);
            },
            onError: (mutationError) => notifSilenceError(mutationError.message),
          })
        }
      />

      <MutationConfirmPreview
        open={applyKycOpen}
        onOpenChange={setApplyKycOpen}
        data={{
          partyId: kycCase.partyId,
          keycloakUserId: party?.keycloakUserId ?? "",
          level: kycCase.levelRequested,
        }}
        title={t("kyc.cases.applyKycApproval.title" as never)}
        description={t("kyc.cases.applyKycApproval.description" as never)}
        confirmLabel={t("kyc.cases.applyKycApproval.action" as never)}
        t={(key) => t(key as never)}
        pending={applyKyc.isPending}
        onConfirm={() => {
          const keycloakUserId = party?.keycloakUserId;
          if (!keycloakUserId) return;
          applyKyc.mutate(
            {
              partyId: kycCase.partyId,
              level: kycCase.levelRequested,
              keycloakUserId,
            },
            {
              onSuccess: () => {
                notifSilence(
                  t("kyc.cases.applyKycApproval.feedback" as never)
                );
                setApplyKycOpen(false);
              },
              onError: (mutationError) =>
                notifSilenceError(apiErrorHandler(mutationError)),
            }
          );
        }}
      />
    </section>
  );
}
